import MomentUtils from "@date-io/moment";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { MuiPickersUtilsProvider, TimePicker } from "@material-ui/pickers";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect } from "react";
import * as yup from "yup";

import HelpAdornment from "../../../InputAdornments/Help";
import * as constants from "../../../../constants";
import patrolWindowElapsedTime from "../../../../utils/patrolWindowElapsedTime";
import ConditionalTooltip from "../../../ConditionalTooltip";

const PatrolWindows = ({ property, disabled, onEdit, onSubmit }) => {
  const { patrolWindows } = property;
  const formik = useFormik({
    initialValues: {
      patrolWindows,
    },
    validationSchema: yup.object({
      patrolWindows: yup.array().of(
        yup.object({
          minsToStart: yup.number(),
          minsToEnd: yup.number(),
          darsRequired: yup.number(),
        })
      ),
    }),
    onSubmit: async (values) => {
      onSubmit(
        {
          propertyId: property.propertyId,
          patrolWindows: values.patrolWindows,
        },
        (property) => {
          formik.resetForm({
            values: {
              patrolWindows: property.patrolWindows,
            },
          });
        }
      );
    },
  });
  useEffect(() => {
    if (formik.dirty) {
      const resetForm = (property) => {
        formik.resetForm({
          values: {
            patrolWindows: property.patrolWindows,
          },
        });
      };
      onEdit(formik.handleSubmit, resetForm);
    }
  }, [formik.dirty]);
  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          patrolWindows: property.patrolWindows,
        },
      });
    }
  }, [property]);
  const classes = useStyles();
  return (
    <div>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5">Patrol Windows</Typography>
            <div className={classes.separator} />
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography color="textSecondary" variant="body2">
              {constants.helpText.patrolWindowExplanation}
            </Typography>
          </Grid>
          {formik.values.patrolWindows.length > 0 ? (
            formik.values.patrolWindows.map((patrolWindow, index) => {
              return (
                <>
                  <Grid
                    key={`patrolWindows[${index}].darsRequired`}
                    item
                    xs={2}
                  >
                    <TextField
                      value={patrolWindow.darsRequired}
                      onChange={formik.handleChange}
                      disabled={disabled}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <HelpAdornment
                            title={constants.helpText.patrolWindowRequiredDars}
                          />
                        ),
                      }}
                      label="Required DARs"
                      name={`patrolWindows[${index}].darsRequired`}
                      type="number"
                      variant="filled"
                    />
                  </Grid>
                  <Grid key={`${patrolWindow.key}:minsToStart`} item xs={3}>
                    <TimePicker
                      value={moment()
                        .startOf("year")
                        .add(patrolWindow.minsToStart, "minutes")}
                      onChange={(moment) => {
                        formik.setFieldValue(
                          `patrolWindows[${index}].minsToStart`,
                          moment.diff(moment.clone().startOf("day"), "minutes")
                        );
                      }}
                      ampm={false}
                      disabled={disabled}
                      format="HH:mm"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <HelpAdornment
                            title={constants.helpText.patrolWindowStartTime}
                          />
                        ),
                      }}
                      inputVariant="filled"
                      label="Start Time"
                    />
                  </Grid>
                  <Grid key={`patrolWindows[${index}].minsToEnd`} item xs={3}>
                    <TimePicker
                      value={moment()
                        .startOf("year")
                        .add(patrolWindow.minsToEnd, "minutes")}
                      onChange={(moment) => {
                        formik.setFieldValue(
                          `patrolWindows[${index}].minsToEnd`,
                          moment.diff(moment.clone().startOf("day"), "minutes")
                        );
                      }}
                      ampm={false}
                      disabled={disabled}
                      format="HH:mm"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <HelpAdornment
                            title={constants.helpText.patrolWindowEndTime}
                          />
                        ),
                      }}
                      inputVariant="filled"
                      label="End Time"
                    />
                  </Grid>
                  <ConditionalTooltip
                    title={constants.helpText.readOnly}
                    visible={!disabled}
                  >
                    <Grid key={`patrolWindows[${index}].elapsed`} item xs={3}>
                      <TextField
                        value={patrolWindowElapsedTime(
                          patrolWindow.minsToStart,
                          patrolWindow.minsToEnd
                        )}
                        disabled
                        fullWidth
                        label="Duration"
                        multiline
                        variant="filled"
                      />
                    </Grid>
                  </ConditionalTooltip>
                  <Grid key={`patrolWindows[${index}].delete`} item xs={1}>
                    <Button
                      onClick={() => {
                        const nextPatrolWindows = [
                          ...formik.values.patrolWindows,
                        ];
                        nextPatrolWindows.splice(index, 1);
                        formik.setFieldValue(
                          "patrolWindows",
                          nextPatrolWindows
                        );
                      }}
                      color="primary"
                      disabled={disabled}
                    >
                      Delete
                    </Button>
                  </Grid>
                </>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">No patrol windows added.</Typography>
            </Grid>
          )}
        </Grid>
      </MuiPickersUtilsProvider>
      <div className={classes.addPatrolWindowButton}>
        <Button
          onClick={() => {
            const nextPatrolWindows = [...formik.values.patrolWindows];
            if (nextPatrolWindows.length > 0) {
              nextPatrolWindows.push({
                ...nextPatrolWindows[nextPatrolWindows.length - 1],
              });
            } else {
              nextPatrolWindows.push({
                darsRequired: 2,
                minsToStart: 360,
                minsToEnd: 360,
              });
            }
            formik.setFieldValue("patrolWindows", nextPatrolWindows);
          }}
          color="primary"
          disabled={disabled}
          variant="contained"
        >
          Add
        </Button>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  separator: {
    height: 8,
  },
  addPatrolWindowButton: {
    marginTop: theme.spacing(2),
  },
}));

export default PatrolWindows;
